"use client"

import { useState } from "react"
import { Box, Typography, Card, CardContent, ToggleButton, ToggleButtonGroup, Paper } from "@mui/material"
import { Assessment } from "@mui/icons-material"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"

// TODO: replace with fetch('/api/csrd/kpis')
const dummyKpisData = [
  {
    metric: "Scope 1 CO₂e",
    A: 120,
    B: 80,
    revA: 0.5,
    revB: 0.4,
    unit: "tCO₂e",
    perRevUnit: "tCO₂e/$M rev",
  },
  {
    metric: "Diversity (% female)",
    A: 45,
    B: 52,
    revA: 45,
    revB: 52,
    unit: "%",
    perRevUnit: "%",
  },
  {
    metric: "Water Usage",
    A: 2500,
    B: 1800,
    revA: 12.5,
    revB: 9.2,
    unit: "m³",
    perRevUnit: "m³/$M rev",
  },
  {
    metric: "Renewable Energy",
    A: 65,
    B: 78,
    revA: 65,
    revB: 78,
    unit: "%",
    perRevUnit: "%",
  },
]

const KpiBenchmark = () => {
  const [isPerRevenueView, setIsPerRevenueView] = useState(false)

  const toggleView = (event, newView) => {
    if (newView !== null) {
      setIsPerRevenueView(newView === "revenue")
    }
  }

  // Determine the yAxis title based on the current view and data availability
  let yAxisTitleText = "Value"
  if (dummyKpisData.length > 0) {
    const firstUnit = isPerRevenueView ? dummyKpisData[0].perRevUnit : dummyKpisData[0].unit
    const allUnitsSame = dummyKpisData.every((kpi) => (isPerRevenueView ? kpi.perRevUnit : kpi.unit) === firstUnit)
    yAxisTitleText = allUnitsSame ? `Value (${firstUnit})` : "Value (Mixed Units)"
  }

  const chartOptions = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      style: {
        fontFamily: "Roboto, sans-serif",
      },
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: dummyKpisData.map((kpi) => kpi.metric),
      crosshair: true,
      labels: {
        style: {
          color: "#707070",
          fontSize: "12px",
        },
      },
      lineColor: "#e0e0e0",
      tickColor: "#e0e0e0",
    },
    yAxis: {
      min: 0,
      title: {
        text: yAxisTitleText,
        style: {
          color: "#707070",
          fontSize: "14px",
          fontWeight: "bold",
        },
      },
      labels: {
        style: {
          color: "#707070",
          fontSize: "12px",
        },
      },
      gridLineColor: "#f0f0f0",
    },
    tooltip: {
      headerFormat: '<span style="font-size:14px;font-weight:bold;color:#407775">{point.key}</span><table>',
      pointFormat:
        '<tr><td style="color:{series.color};padding:0;padding-right:10px">{series.name}: </td>' +
        '<td style="padding:0"><b>{point.y:.1f} ' +
        (isPerRevenueView ? dummyKpisData[0].perRevUnit : dummyKpisData[0].unit) +
        "</b></td></tr>",
      footerFormat: "</table>",
      shared: true,
      useHTML: true,
      backgroundColor: "white",
      borderColor: "#407775",
      borderRadius: 8,
      shadow: true,
    },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0,
        borderRadius: 4,
        dataLabels: {
          enabled: true,
          style: {
            color: "#707070",
            fontSize: "11px",
            fontWeight: "bold",
          },
        },
      },
    },
    series: [
      {
        name: "Company A",
        data: dummyKpisData.map((kpi) => (isPerRevenueView ? kpi.revA : kpi.A)),
        color: "#407775",
      },
      {
        name: "Company B",
        data: dummyKpisData.map((kpi) => (isPerRevenueView ? kpi.revB : kpi.B)),
        color: "#FAB430",
      },
    ],
    credits: {
      enabled: false,
    },
    legend: {
      itemStyle: {
        color: "#707070",
        fontSize: "12px",
      },
    },
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#fff", minHeight: "100vh" }}>
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            color: "#407775",
            fontWeight: "bold",
            mb: 1,
          }}
        >
          KPI Benchmark Analysis
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#707070",
            maxWidth: "600px",
            mx: "auto",
          }}
        >
          Compare key performance indicators across companies with absolute values or revenue-normalized metrics
        </Typography>
      </Box>

      {/* Controls */}
      <Card
        elevation={0}
        sx={{
          mb: 4,
          border: "1px solid #e0e0e0",
          borderRadius: "12px",
        }}
      >
        <CardContent sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
          <Typography variant="body1" sx={{ color: "#707070", fontWeight: "medium" }}>
            View Mode:
          </Typography>
          <ToggleButtonGroup
            value={isPerRevenueView ? "revenue" : "absolute"}
            exclusive
            onChange={toggleView}
            sx={{
              "& .MuiToggleButton-root": {
                border: "1px solid #407775",
                color: "#407775",
                textTransform: "none",
                "&.Mui-selected": {
                  backgroundColor: "#407775",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#2E5266",
                  },
                },
                "&:hover": {
                  backgroundColor: "#f0f7f7",
                },
              },
            }}
          >
            <ToggleButton value="absolute">Absolute Values</ToggleButton>
            <ToggleButton value="revenue">Per $M Revenue</ToggleButton>
          </ToggleButtonGroup>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {dummyKpisData.length > 0 ? (
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: "center",
                backgroundColor: "#f5f5f5",
                border: "1px dashed #ccc",
                borderRadius: "8px",
              }}
            >
              <Assessment sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
              <Typography
                variant="h6"
                sx={{
                  color: "#707070",
                  fontStyle: "italic",
                }}
              >
                No KPI data available to display
              </Typography>
            </Paper>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}

export default KpiBenchmark
